import { ChatBubbleOvalLeftEllipsisIcon } from "@heroicons/react/24/solid";
import * as Popover from "@radix-ui/react-popover";
import clsx from "clsx";
import { AnimatePresence, type AnimationProps, motion, useReducedMotion } from "framer-motion";
import React, { forwardRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { Drawer } from "vaul";

import { useGetChatThreadsQuery } from "~/api";
import { ZIndex } from "~/common/constants";
import { Badge } from "~/components/ui/badge";
import { SupportMiniChat } from "~/features/chat/support-mini-chat";

export function SupportWidget() {
  const { pathname } = useLocation();
  // Full chat page already is the chat UI — hide the floating FAB/popover there.
  if (pathname === "/pages/chat" || pathname.startsWith("/pages/chat/")) {
    return null;
  }

  return (
    // `support-widget-rail` is inert (`display: contents`) on legacy — the FAB stays viewport-fixed.
    // On a centred redesign shell it becomes a fixed, centred, max-width, click-through layer so the
    // FAB anchors to the CONTENT block's right edge instead of the window's (see base.css / ADR-0011).
    <div className="support-widget-rail tw-contents">
      <div className="legacy-support-widget sm:tw-hidden">
        <SupportWidgetMobile />
      </div>

      <div className="legacy-support-widget max-sm:tw-hidden">
        <SupportWidgetDesktop />
      </div>
    </div>
  );
}

export function SupportWidgetMobile() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Drawer.Root open={isOpen} onOpenChange={setIsOpen}>
      <Drawer.Trigger asChild>
        <SupportWidgetTrigger />
      </Drawer.Trigger>

      <Drawer.Portal>
        <Drawer.Overlay
          className="tw-fixed tw-inset-0 tw-bg-black/40 sm:tw-hidden"
          style={{ zIndex: ZIndex.SUPPORT_WIDGET + 1 }}
        />
        <Drawer.Content
          className={clsx(
            "tw-fixed tw-inset-x-0 tw-bottom-0 tw-mt-24 tw-flex tw-h-fit tw-max-h-[90vh] tw-flex-col",
            "tw-rounded-t-[16px] tw-bg-transparent tw-shadow-none sm:tw-hidden",
          )}
          style={{ zIndex: ZIndex.SUPPORT_WIDGET + 2 }}
        >
          <div className="tw-relative tw-flex tw-max-h-[min(90vh,680px)] tw-min-h-[320px] tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-t-[16px] tw-bg-white tw-px-4 tw-pb-4 tw-pt-2">
            <div
              aria-hidden
              className="tw-mx-auto tw-mb-2 tw-h-1.5 tw-w-12 tw-shrink-0 tw-rounded-full tw-bg-gray-300"
            />
            <SupportWidgetContent open={isOpen} onClose={() => setIsOpen(false)} />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export function SupportWidgetDesktop() {
  const [isOpen, setIsOpen] = useState(false);

  const animationProps: AnimationProps = {
    initial: { opacity: 0, y: 50 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, transition: { duration: 0.15 } },
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <SupportWidgetTrigger />
      </Popover.Trigger>

      <AnimatePresence>
        {isOpen ? (
          <Popover.Portal forceMount>
            <Popover.Content
              className={clsx(
                "tw-relative tw-right-4 tw-rounded-none tw-border-0 tw-bg-transparent tw-p-0 tw-shadow-none",
                "max-sm:tw-hidden lg:tw-bottom-1 lg:tw-right-6",
              )}
              side="bottom"
              sideOffset={12}
              align="end"
              asChild
              style={{ zIndex: ZIndex.SUPPORT_WIDGET + 2 }}
            >
              <motion.div {...animationProps} className="tw-outline-none">
                <SupportWidgetContent open={isOpen} onClose={() => setIsOpen(false)} />
              </motion.div>
            </Popover.Content>
          </Popover.Portal>
        ) : null}
      </AnimatePresence>
    </Popover.Root>
  );
}

const SupportWidgetTrigger = forwardRef<HTMLButtonElement>(function Trigger(props, forwardedRef) {
  const shouldReduceMotion = useReducedMotion();
  const intl = useIntl();
  const { data } = useGetChatThreadsQuery(undefined, { pollingInterval: 5000 });
  const unreadTotal = (data?.threads ?? []).reduce((sum, t) => sum + (t.unread_count ?? 0), 0);

  const animationProps: AnimationProps = {
    initial: { opacity: 0, scale: 0 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0 },
  };

  return (
    <motion.button
      ref={forwardedRef}
      {...props}
      {...(shouldReduceMotion == true ? {} : animationProps)}
      className={clsx(
        "support-widget-fab tw-fixed tw-bottom-4 tw-right-4 tw-h-16 tw-w-16 tw-overflow-visible tw-rounded-[20px] tw-p-0",
        "tw-border-2 tw-border-solid tw-border-brand-green tw-bg-brand-green",
        "lg:tw-bottom-8 lg:tw-right-8",
        "tw-group tw-shadow-md tw-transition-all",
        "hover:tw-border-brand-green-hover hover:tw-bg-brand-green-hover active:tw-scale-[0.85]",
      )}
      style={{ zIndex: ZIndex.SUPPORT_WIDGET, borderRadius: 20 }}
    >
      {unreadTotal > 0 ? (
        <Badge
          srOnlyText={intl.formatMessage({ id: "chat.unread_total" }, { count: unreadTotal })}
          className="tw-pointer-events-none tw-absolute -tw-right-2 -tw-top-2 tw-z-10"
        >
          {unreadTotal > 99 ? "99+" : unreadTotal}
        </Badge>
      ) : null}
      <ChatBubbleOvalLeftEllipsisIcon className="tw-h-8 tw-w-8 tw-text-white tw-transition-colors group-hover:tw-text-gray-200" />
    </motion.button>
  );
});

function SupportWidgetContent({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <div className="tw-flex tw-h-[min(78vh,620px)] tw-w-full tw-max-w-[min(100vw-1.5rem,400px)] tw-flex-col tw-overflow-hidden tw-rounded-2xl sm:tw-h-[min(72vh,600px)] sm:tw-w-[400px]">
      <SupportMiniChat open={open} onClose={onClose} />
    </div>
  );
}
